/**
 * Shared types and pure helpers for the Venue Analytics page (chart, table
 * and per-venue summary cards all read from the same points).
 */

export interface VenueRef {
  id: number;
  name: string;
}

export interface AttendancePoint {
  venueId: number;
  venueName: string;
  eventId: string;
  date: string;
  eventLabel: string;
  teamCount: number;
  peopleCount: number;
}

export type MetricKind = 'teams' | 'people' | 'avg';

// Window size for the trailing moving average ("regular attendance" line).
export const ROLLING_WINDOW = 4;

// A small, color-blind-friendly palette cycled across venues.
const SERIES_COLORS: readonly string[] = [
  'var(--blue-9)',
  'var(--amber-9)',
  'var(--green-9)',
  'var(--purple-9)',
  'var(--tomato-9)',
  'var(--cyan-9)',
  'var(--pink-9)',
  'var(--orange-9)',
];
const FALLBACK_COLOR = 'var(--blue-9)';

export function colorForVenue(venueId: number, venues: VenueRef[]): string {
  const idx = venues.findIndex((v) => v.id === venueId);
  return SERIES_COLORS[(idx < 0 ? 0 : idx) % SERIES_COLORS.length] ?? FALLBACK_COLOR;
}

export function formatDate(s: string | number): string {
  return new Date(s).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// "Wed, Sep 23" for tooltip rows and table cells, where the year is noise.
export function formatDayDate(s: string | number): string {
  return new Date(s).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function valueFor(p: AttendancePoint, kind: MetricKind): number {
  if (kind === 'teams') {
    return p.teamCount;
  }
  if (kind === 'people') {
    return p.peopleCount;
  }
  return p.teamCount > 0 ? p.peopleCount / p.teamCount : 0;
}

export function metricLabel(kind: MetricKind): string {
  if (kind === 'teams') {
    return 'teams';
  }
  if (kind === 'people') {
    return 'people';
  }
  return 'per team';
}

export function formatValue(v: number, kind: MetricKind): string {
  return kind === 'avg' ? v.toFixed(1) : String(Math.round(v));
}

// Trailing moving average aligned with `pts`: entry i is the mean of the
// metric over events i-window+1..i, or null until `window` events exist.
export function rollingAligned(
  pts: AttendancePoint[],
  kind: MetricKind,
  window: number
): (number | null)[] {
  return pts.map((_, i) => {
    if (i < window - 1) {
      return null;
    }
    let sum = 0;
    for (let j = i - window + 1; j <= i; j += 1) {
      const pj = pts[j];
      if (pj) {
        sum += valueFor(pj, kind);
      }
    }
    return sum / window;
  });
}

export interface SmoothPoint {
  t: number;
  value: number;
}

// Least-squares linear regression of the metric against time. Returns the two
// endpoints of the best-fit line (at the first and last event times). Needs at
// least 2 points. Degenerate (all-same-time) inputs return null.
export function regressionLine(
  pts: AttendancePoint[],
  kind: MetricKind
): [SmoothPoint, SmoothPoint] | null {
  if (pts.length < 2) {
    return null;
  }
  const n = pts.length;
  const xs = pts.map((p) => new Date(p.date).getTime());
  const ys = pts.map((p) => valueFor(p, kind));
  const meanX = xs.reduce((s, v) => s + v, 0) / n;
  const meanY = ys.reduce((s, v) => s + v, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i += 1) {
    const dx = (xs[i] ?? 0) - meanX;
    num += dx * ((ys[i] ?? 0) - meanY);
    den += dx * dx;
  }
  if (den === 0) {
    return null;
  }
  const slope = num / den;
  const intercept = meanY - slope * meanX;
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  return [
    { t: x0, value: intercept + slope * x0 },
    { t: x1, value: intercept + slope * x1 },
  ];
}

// Group points by venue, keeping the API's oldest-first order in each list.
export function groupByVenue(points: AttendancePoint[]): Map<number, AttendancePoint[]> {
  const byVenue = new Map<number, AttendancePoint[]>();
  for (const p of points) {
    const list = byVenue.get(p.venueId) ?? [];
    list.push(p);
    byVenue.set(p.venueId, list);
  }
  return byVenue;
}

export interface VenueSummary {
  venue: VenueRef;
  color: string;
  nights: number;
  teamsPerNight: number;
  peoplePerNight: number;
  peoplePerTeam: number;
  // Mean teams over the last ROLLING_WINDOW nights minus the mean over the
  // ROLLING_WINDOW nights before that. Null until there are 2 full windows.
  recentTeamsDelta: number | null;
}

function meanTeams(pts: AttendancePoint[]): number {
  return pts.reduce((s, p) => s + p.teamCount, 0) / pts.length;
}

export function summarizeVenues(
  points: AttendancePoint[],
  venues: VenueRef[]
): VenueSummary[] {
  const byVenue = groupByVenue(points);
  const out: VenueSummary[] = [];
  for (const venue of venues) {
    const pts = byVenue.get(venue.id);
    if (!pts || pts.length === 0) {
      continue;
    }
    const teams = pts.reduce((s, p) => s + p.teamCount, 0);
    const people = pts.reduce((s, p) => s + p.peopleCount, 0);
    const w = ROLLING_WINDOW;
    out.push({
      venue,
      color: colorForVenue(venue.id, venues),
      nights: pts.length,
      teamsPerNight: teams / pts.length,
      peoplePerNight: people / pts.length,
      peoplePerTeam: teams > 0 ? people / teams : 0,
      recentTeamsDelta:
        pts.length >= 2 * w
          ? meanTeams(pts.slice(-w)) - meanTeams(pts.slice(-2 * w, -w))
          : null,
    });
  }
  return out;
}

// A "nice" gridline step (1, 2, 2.5 or 5 × 10^n) that splits `max` into
// roughly `count` intervals.
export function niceStep(max: number, count: number): number {
  const raw = max / count;
  if (raw <= 0) {
    return 1;
  }
  const mag = 10 ** Math.floor(Math.log10(raw));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (m * mag >= raw) {
      return m * mag;
    }
  }
  return 10 * mag;
}

// First-of-month timestamps between minT and maxT, thinned so that at most
// `maxTicks` labels render.
export function monthTicks(minT: number, maxT: number, maxTicks: number): number[] {
  const ticks: number[] = [];
  const d = new Date(minT);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  if (d.getTime() < minT) {
    d.setMonth(d.getMonth() + 1);
  }
  while (d.getTime() <= maxT) {
    ticks.push(d.getTime());
    d.setMonth(d.getMonth() + 1);
  }
  const every = Math.max(1, Math.ceil(ticks.length / maxTicks));
  return ticks.filter((_, i) => i % every === 0);
}
